import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Save, Building2, Landmark, ExternalLink, Hash } from "lucide-react";

/**
 * Small helper that renders current India + LLC counters and lets admin
 * override the CURRENT-period seq (e.g. mid-year takeover from another system).
 */
function InvoiceSeriesCounters() {
  const [data, setData] = useState(null);
  const [entity, setEntity] = useState("india");
  const [resetSeq, setResetSeq] = useState("");
  const load = () => { api.get("/api/invoice-series").then((r) => setData(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);
  const applyReset = async () => {
    const n = Number(resetSeq);
    if (!Number.isFinite(n) || n < 0) return toast.error("Enter a valid number");
    try {
      await api.patch("/api/invoice-series", { entity, reset_seq: n });
      toast.success(`Next ${entity.toUpperCase()} invoice will be #${n + 1}`);
      setResetSeq("");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Reset failed")); }
  };
  if (!data) return null;
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-md bg-brand/10 grid place-items-center shrink-0"><Hash className="w-4 h-4 text-brand" /></div>
        <div>
          <div className="font-heading font-semibold text-sm text-slate-800">Invoice Number Series</div>
          <div className="text-xs text-slate-500">India: <code>Prefix/FY/MMM/NNN</code> (resets 1 Apr) · LLC: <code>Prefix/YY/NNN</code> (resets 1 Jan)</div>
        </div>
      </div>
      <div className="text-xs mb-3">
        <div className="grid grid-cols-3 gap-2 font-semibold text-slate-500 uppercase tracking-wider">
          <div>Period</div><div>Last Used Seq</div><div>Updated</div>
        </div>
        {(data.counters || []).map((c) => (
          <div key={c.key} className="grid grid-cols-3 gap-2 py-1 border-t border-slate-100 text-slate-700">
            <div><code>{c.key}</code></div>
            <div>{c.seq}</div>
            <div className="text-slate-500">{c.updated_at ? new Date(c.updated_at).toLocaleString() : "—"}</div>
          </div>
        ))}
        {!(data.counters || []).length && <div className="text-slate-500 mt-1 italic">No invoices generated yet — the first one will be seq 001.</div>}
      </div>
      <div className="flex flex-wrap items-end gap-2 pt-3 border-t border-slate-100">
        <div className="text-xs text-slate-500">Manually reset the CURRENT-period counter:</div>
        <select value={entity} onChange={(e) => setEntity(e.target.value)} className="h-8 text-sm border border-slate-200 rounded px-2" data-testid="series-entity">
          <option value="india">India</option>
          <option value="llc">LLC</option>
        </select>
        <Input value={resetSeq} onChange={(e) => setResetSeq(e.target.value)} placeholder="Set last-used seq (e.g. 56)"
          className="h-8 text-sm w-56" data-testid="series-reset-value" />
        <Button size="sm" variant="outline" onClick={applyReset} data-testid="series-reset-btn">Apply reset</Button>
      </div>
    </div>
  );
}

/**
 * Admin-only editor for invoice/company settings that drive the PDF templates:
 * - Brand + address block (used in both Indian and LLC invoices)
 * - GSTIN / phone / email / PayPal handle
 * - India (HDFC) bank block
 * - LLC (Choice Financial Group) bank block
 * - Footer note
 *
 * Fields all persist under `db.settings.company` via PATCH /api/settings/company.
 */
const Field = ({ label, testId, ...props }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-slate-600">{label}</Label>
    <Input {...props} data-testid={testId} className="h-9 text-sm" />
  </div>
);

const SectionTitle = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-4">
    <div className="w-9 h-9 rounded-md bg-brand/10 grid place-items-center shrink-0"><Icon className="w-4 h-4 text-brand" /></div>
    <div>
      <div className="font-heading font-semibold text-slate-800">{title}</div>
      {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
    </div>
  </div>
);

export default function CompanySettingsCard() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/api/settings/company")
      .then((r) => setForm(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Could not load company settings")))
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/api/settings/company", form);
      setForm(data);
      toast.success("Company settings saved — new PDFs will use these values");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not save settings"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <Card className="border-slate-200 shadow-none p-6" data-testid="company-settings-card">
      <div className="text-sm text-slate-500">Loading company settings…</div>
    </Card>
  );
  if (!form) return null;

  return (
    <Card className="border-slate-200 shadow-none p-6" data-testid="company-settings-card">
      <div className="flex items-center justify-between mb-1">
        <div className="font-heading font-medium">Invoice / Company Settings</div>
        <a
          href="https://www.fbil.org.in/#/home"
          target="_blank" rel="noreferrer"
          className="text-xs text-brand hover:underline inline-flex items-center gap-1"
        >
          FBIL rates <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <div className="text-sm text-slate-500 mb-6">
        These values populate the header, bank block and footer of every printed invoice PDF
        (both Indian and LLC templates). Changes take effect immediately.
      </div>

      {/* ---------- BRAND / ADDRESS ---------- */}
      <SectionTitle icon={Building2} title="Company Identity" subtitle="Appears at the top of every invoice" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Brand Name" value={form.brand_name || ""} onChange={set("brand_name")} testId="cs-brand-name" />
        <Field label="GSTIN (India template)" value={form.gstin || ""} onChange={set("gstin")} testId="cs-gstin" />
        <Field label="Email" value={form.email || ""} onChange={set("email")} testId="cs-email" />
        <Field label="Phone" value={form.phone || ""} onChange={set("phone")} testId="cs-phone" />
        <Field label="USA Label" value={form.usa_label || ""} onChange={set("usa_label")} testId="cs-usa-label" />
        <Field label="India Label" value={form.india_label || ""} onChange={set("india_label")} testId="cs-india-label" />
        <div className="md:col-span-2">
          <Label className="text-xs font-medium text-slate-600">USA Address (LLC template only)</Label>
          <Textarea value={form.usa_address || ""} onChange={set("usa_address")} rows={2} className="text-sm" data-testid="cs-usa-address" />
        </div>
        <div>
          <Label className="text-xs font-medium text-slate-600">India Address — Line 1</Label>
          <Textarea value={form.india_address_line1 || ""} onChange={set("india_address_line1")} rows={2} className="text-sm" data-testid="cs-india-addr1" />
        </div>
        <div>
          <Label className="text-xs font-medium text-slate-600">India Address — Line 2</Label>
          <Textarea value={form.india_address_line2 || ""} onChange={set("india_address_line2")} rows={2} className="text-sm" data-testid="cs-india-addr2" />
        </div>
        <Field label="PayPal Handle (India template)" value={form.paypal_handle || ""} onChange={set("paypal_handle")} testId="cs-paypal" />
        <Field label="India Invoice Prefix" value={form.india_invoice_prefix || "TS"} onChange={set("india_invoice_prefix")} testId="cs-india-prefix" />
        <Field label="LLC Invoice Prefix" value={form.llc_invoice_prefix || "TS"} onChange={set("llc_invoice_prefix")} testId="cs-llc-prefix" />
      </div>

      <div className="mt-6 border-t border-slate-100 pt-4">
        <InvoiceSeriesCounters />
      </div>

      {/* ---------- INDIA BANK ---------- */}
      <div className="mt-8 border-t border-slate-100 pt-6">
        <SectionTitle icon={Landmark} title="India Bank Details" subtitle="Shown under 'Direct Transfer OR Transferwise' on Indian invoices" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Name of Bank" value={form.in_bank_name || ""} onChange={set("in_bank_name")} testId="cs-in-bank" />
          <Field label="A/C Name" value={form.in_account_name || ""} onChange={set("in_account_name")} testId="cs-in-acname" />
          <Field label="A/C No." value={form.in_account_number || ""} onChange={set("in_account_number")} testId="cs-in-acno" />
          <Field label="IFSC" value={form.in_ifsc || ""} onChange={set("in_ifsc")} testId="cs-in-ifsc" />
          <Field label="SWIFT Code" value={form.in_swift || ""} onChange={set("in_swift")} testId="cs-in-swift" />
          <Field label="Branch" value={form.in_branch || ""} onChange={set("in_branch")} testId="cs-in-branch" />
        </div>
      </div>

      {/* ---------- LLC BANK ---------- */}
      <div className="mt-8 border-t border-slate-100 pt-6">
        <SectionTitle icon={Landmark} title="LLC Bank Details" subtitle="Shown under 'ACH Transfer OR Wire Transfer' on LLC invoices" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Name of Bank" value={form.llc_bank_name || ""} onChange={set("llc_bank_name")} testId="cs-llc-bank" />
          <Field label="Beneficiary Name" value={form.llc_beneficiary_name || ""} onChange={set("llc_beneficiary_name")} testId="cs-llc-beneficiary" />
          <Field label="A/C No." value={form.llc_account_number || ""} onChange={set("llc_account_number")} testId="cs-llc-acno" />
          <Field label="Type of Account" value={form.llc_account_type || ""} onChange={set("llc_account_type")} testId="cs-llc-actype" />
          <Field label="ABA Routing Number" value={form.llc_aba_routing || ""} onChange={set("llc_aba_routing")} testId="cs-llc-aba" />
          <div className="md:col-span-1">
            <Label className="text-xs font-medium text-slate-600">Bank Address</Label>
            <Textarea value={form.llc_bank_address || ""} onChange={set("llc_bank_address")} rows={2} className="text-sm" data-testid="cs-llc-bankaddr" />
          </div>
        </div>
      </div>

      {/* ---------- FOOTER NOTE ---------- */}
      <div className="mt-8 border-t border-slate-100 pt-6">
        <Label className="text-xs font-medium text-slate-600">Footer Note (appears at the bottom of every invoice)</Label>
        <Input value={form.footer_note || ""} onChange={set("footer_note")} className="h-9 text-sm mt-1.5" data-testid="cs-footer" />
      </div>

      <div className="mt-8 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover" data-testid="cs-save-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save Company Settings"}
        </Button>
      </div>
    </Card>
  );
}
